import { useCallback, useEffect, useState } from "react";
import { ShieldCheck } from "lucide-react";
import api, { fmtDate, fmtErr } from "../../lib/api";
import { toast } from "sonner";

const ROLE_CLS = {
  super_admin: "bg-shipany-navy text-white", admin: "bg-shipany-blue text-white", operations: "bg-blue-50 text-blue-700",
  finance: "bg-emerald-50 text-emerald-700", support: "bg-amber-50 text-amber-700", warehouse_manager: "bg-violet-50 text-violet-700",
  warehouse_staff: "bg-violet-50 text-violet-600", customer_admin: "bg-slate-100 text-slate-600", customer_staff: "bg-slate-100 text-slate-500",
  developer: "bg-cyan-50 text-cyan-700", customer: "bg-slate-100 text-slate-500",
};

export default function AdminTeam() {
  const [data, setData] = useState(null);
  const { roles, members } = data || { roles: [], members: [] };

  const load = useCallback(() => api.get("/admin/team").then(({ data }) => setData(data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const setRole = async (m, role) => {
    try {
      await api.put(`/admin/team/${m.user_id}/role`, { role });
      toast.success(`${m.email} → ${role}`);
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  return (
    <div data-testid="admin-team-page">
      <div className="flex items-center gap-3">
        <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-shipany-navy text-shipany-cyan"><ShieldCheck className="h-5 w-5" /></span>
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight text-shipany-navy">Team & Roles</h1>
          <p className="text-sm text-slate-500">Enterprise RBAC — fine-grained roles across operations, finance, support, warehouse and developers.</p>
        </div>
      </div>

      <div className="mt-6 overflow-x-auto rounded-2xl border border-slate-200 bg-white" data-testid="team-table">
        <table className="w-full min-w-[720px] text-sm">
          <thead>
            <tr className="bg-shipany-navy text-left text-xs uppercase tracking-wider text-white">
              <th className="px-5 py-3.5">Member</th><th className="px-4 py-3.5">Company</th><th className="px-4 py-3.5">Auth</th><th className="px-4 py-3.5">Joined</th><th className="px-4 py-3.5">Role</th>
            </tr>
          </thead>
          <tbody>
            {members.map((m) => (
              <tr key={m.user_id} className="border-t border-slate-100" data-testid={`team-row-${m.email}`}>
                <td className="px-5 py-3"><p className="font-semibold text-shipany-navy">{m.name}</p><p className="text-xs text-slate-400">{m.email}</p></td>
                <td className="px-4 py-3 text-xs text-slate-500">{m.company || "—"}</td>
                <td className="px-4 py-3"><span className="rounded bg-slate-100 px-2 py-0.5 text-[10px] font-bold uppercase text-slate-500">{m.auth_provider || "jwt"}</span></td>
                <td className="px-4 py-3 text-xs text-slate-500">{fmtDate(m.created_at)}</td>
                <td className="px-4 py-3">
                  <select value={m.role} onChange={(e) => setRole(m, e.target.value)} data-testid={`role-select-${m.email}`}
                    className={`rounded-lg px-2.5 py-1.5 text-xs font-bold focus:outline-none ${ROLE_CLS[m.role] || ROLE_CLS.customer}`}>
                    <option value="customer">customer</option>
                    {roles.map((r) => <option key={r} value={r}>{r.replace(/_/g, " ")}</option>)}
                  </select>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
